<template>
    <!-- Header avec gradient personnalisé -->
    <header class="header relative overflow-hidden text-white py-20 text-center">
        <div class="container mx-auto px-4">
            <div class="relative ">
                <h1 class="text-4xl md:text-6xl font-bold mb-4 tracking-tight">Collections</h1>
                <p class="text-lg md:text-xl opacity-90 max-w-2xl mx-auto leading-relaxed">
                    Découvrez nos sélections thématiques de livres et ressources soigneusement organisées pour enrichir votre savoir
                </p>
            </div>
        </div>
    </header>

    <!-- Filtres et recherche -->
    <section class="bg-gray-50 border-b border-gray-200 py-6">
        <div class="max-w-6xl mx-auto px-4">
            <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <!-- Barre de recherche -->
                <div class="flex-1 relative">
                    <input
                        v-model="search"
                        type="text"
                        placeholder="Rechercher une collection"
                        class="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#2A3992] focus:border-[#2A3992] text-gray-700 transition-all duration-200"
                    />
                    <svg xmlns="http://www.w3.org/2000/svg"
                         fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                         stroke="currentColor"
                         class="w-6 h-6 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                </div>

                <!-- Options d'affichage -->
            </div>
        </div>
    </section>

    <!-- Section principale des collections -->
    <main class="bg-gray-50 py-20">
        <div class="max-w-6xl mx-auto px-4">
            <!-- Titre de section -->
            <div class="mb-12">
                <h2 class="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-4">Nos collections</h2>
                <p class="text-lg text-gray-600 mx-auto text-center max-w-3xl">
                    Explorez nos différentes thématiques et trouvez les ressources qui correspondent à vos centres d'intérêt
                </p>
            </div>

            <!-- Grille des collections -->
            <div class="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                <div
                    v-for="collection in filteredCollections"
                    :key="collection.id"
                    class="collection-card relative bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-2"
                >
                    <!-- Lien invisible qui couvre toute la card -->
                    <a
                        :href="route('show-collection', { collection: collection.id })"
                        class="absolute inset-0 z-10"
                        aria-hidden="true"
                    ></a>

                    <!-- Image de la collection -->
                    <div class="relative h-56 overflow-hidden pointer-events-none">
                        <img
                            :src="collection.cover"
                            :alt="collection.nom"
                            class="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                        />
                        <span class="absolute top-4 left-4 bg-[#2A3992] text-white text-sm font-medium px-3 py-1 rounded-full shadow-md">
                        Collection
                      </span>
                    </div>

                    <!-- Contenu de la carte -->
                    <div class="p-6 pointer-events-none">
                        <!-- Métadonnées -->
                        <div class="flex justify-between items-center text-sm text-gray-500 mb-3">
                            <span class="flex items-center gap-1">
                              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                              </svg>
                              {{ collection.book_count }} ouvrages
                            </span>
                            <span>{{ collection.created_at }}</span>
                        </div>

                        <!-- Titre et description -->
                        <h3 class="text-xl font-bold text-gray-800 mb-3 line-clamp-2">
                            {{ collection.nom }}
                        </h3>
                        <p v-if="collection.description" class="text-gray-600 mb-6 leading-relaxed line-clamp-3">
                            {{ collection.description?.substring(0, 120) + (collection.description?.length > 120 ? '...' : '') }}
                        </p>

                        <!-- Bouton d'action (toujours cliquable car dans le lien) -->
                        <span
                            class="block w-full text-center bg-white border-2 border-[#2A3992] text-[#2A3992] font-semibold px-4 py-3 rounded-lg hover:bg-[#2A3992] hover:text-white transition-all duration-300 hover:shadow-md"
                        >
                         Voir les ouvrages
                      </span>
                    </div>
                </div>
            </div>

            <!-- Message si aucune collection trouvée -->
            <div v-if="!filteredCollections.length && search" class="text-center py-16">
                <div class="max-w-md mx-auto">
                    <svg class="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                    </svg>
                    <h3 class="text-lg font-medium text-gray-900 mb-2">Aucune collection trouvée</h3>
                    <p class="text-gray-500">Essayez de modifier vos critères de recherche.</p>
                </div>
            </div>
        </div>
    </main>
</template>

<script setup>
import { route } from "@/helper.js";
import { computed, ref } from "vue";

const props = defineProps({
    collections: Object
});

const search = ref('');

const filteredCollections = computed(() => {
    if (!search.value) {
        return props.collections;
    }
    return props.collections.filter(collection =>
        collection.nom.toLowerCase().includes(search.value.toLowerCase()) ||
        collection.description?.toLowerCase().includes(search.value.toLowerCase())
    );
});
</script>

<style scoped>
/* Header avec gradient personnalisé */
.header {
    background: linear-gradient(135deg, #1e3a8a 0%, #032c70 50%, #03414c 100%);
}

.header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="books" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse"><rect width="2" height="15" x="2" y="2" fill="white" opacity="0.1"/><rect width="2" height="12" x="6" y="5" fill="white" opacity="0.1"/><rect width="2" height="18" x="10" y="1" fill="white" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23books)"/></svg>') repeat;
}

/* Utilitaires personnalisés pour le texte tronqué */
.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

/* Animation subtile pour les cartes */
.collection-card {
    background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
}
</style>
